/**
 * Stay-points that happened while this mission was moving.
 *
 * Tracks smear a stop into a line. This tab is the list of places someone
 * actually lingered — our kit first, then anyone else who stayed on the
 * planned line in the same window. It is activity, not a threat, so the
 * visual weight goes to duration: the bar under each stay is sized against
 * the longest stay on the run.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { MissionDwell, listMissionDwells } from '../../services/missionApi';
import { formatSpan, formatWhen } from './format';
import { MUTED, TEAL } from './missionLiveParts';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/PersonOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TimerIcon from '@mui/icons-material/TimerOutlined';

const GOLD = '#f5b942';
const EDGE = '#262b33';

export interface MissionDwellsTabProps {
  missionId: number;
  started?: boolean;
  refreshKey?: number;
  onFocus?: (longitude: number, latitude: number) => void;
  onCount?: (count: number) => void;
}

const MissionDwellsTab: React.FC<MissionDwellsTabProps> = ({
  missionId,
  started = true,
  refreshKey,
  onFocus,
  onCount,
}) => {
  const [dwells, setDwells] = useState<MissionDwell[]>([]);

  useEffect(() => {
    if (!started) {
      setDwells([]);
      onCount?.(0);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      listMissionDwells(missionId)
        .then((rows) => {
          if (cancelled) return;
          setDwells(rows);
          onCount?.(rows.length);
        })
        .catch(() => {
          if (cancelled) return;
          setDwells([]);
          onCount?.(0);
        });
    };
    load();
    const timer = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [missionId, started, refreshKey, onCount]);

  const stats = useMemo(() => {
    const own = dwells.filter((d) => d.own).length;
    const longest = dwells.reduce((max, d) => Math.max(max, d.duration_s || 0), 0);
    const total = dwells.reduce((sum, d) => sum + (d.duration_s || 0), 0);
    const active = dwells.filter((d) => d.start_at && !d.end_at).length;
    const long = dwells.filter((d) => (d.duration_s || 0) >= 300).length;
    const places = new Set(dwells.map((d) => d.place_name || d.label).filter(Boolean)).size;
    return { own, observed: dwells.length - own, longest, total, active, long, places };
  }, [dwells]);

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
        Dwells
      </Typography>
      <DockTabHelp tab="dwells" />
    </Box>
  );

  if (!started) {
    return (
      <Box>
        {title}
        <Typography variant="caption" color="text.secondary">
          Start the mission to collect stay-points along the run.
        </Typography>
      </Box>
    );
  }

  if (dwells.length === 0) {
    return (
      <Box>
        {title}
        <Typography variant="caption" color="text.secondary">
          No dwells on this run yet. Stops longer than two minutes will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title}
      <DockStatStrip
        items={[
          { label: 'Stays', value: dwells.length, color: '#e8ebee', icon: <PlaceIcon /> },
          { label: 'Our kit', value: stats.own, color: TEAL, icon: <PersonIcon /> },
          { label: 'Observed', value: stats.observed, color: GOLD, icon: <VisibilityIcon /> },
          { label: 'Active', value: stats.active, color: stats.active ? TEAL : MUTED },
          { label: 'Longest', value: stats.longest ? formatSpan(stats.longest) : '—', color: GOLD, icon: <TimerIcon /> },
          { label: 'Total', value: stats.total ? formatSpan(stats.total) : '—', color: MUTED },
          { label: '>5 min', value: stats.long, color: '#ef5350' },
          { label: 'Places', value: stats.places, color: '#42a5f5' },
        ]}
      />

      {dwells.map((dwell) => (
        <DwellCard
          key={String(dwell.id)}
          dwell={dwell}
          longest={stats.longest}
          onFocus={onFocus}
        />
      ))}
    </Box>
  );
};

const DwellCard: React.FC<{
  dwell: MissionDwell;
  longest: number;
  onFocus?: (longitude: number, latitude: number) => void;
}> = ({ dwell, longest, onFocus }) => {
  const tone = dwell.own ? TEAL : GOLD;
  const pct = longest > 0 && dwell.duration_s
    ? Math.max(6, Math.round((dwell.duration_s / longest) * 100))
    : 0;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onFocus?.(dwell.longitude, dwell.latitude)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFocus?.(dwell.longitude, dwell.latitude);
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: '3px 1fr',
        gap: 1,
        mb: 0.75,
        borderRadius: 1,
        cursor: onFocus ? 'pointer' : 'default',
        '&:hover': onFocus ? { bgcolor: 'rgba(255,255,255,0.03)' } : undefined,
      }}
    >
      <Box sx={{ borderRadius: '0 2px 2px 0', bgcolor: tone, opacity: 0.85 }} />
      <Box sx={{ py: 0.45, pr: 0.5, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#e8ebee' }}
            noWrap
          >
            {dwell.place_name || dwell.place_address || dwell.label}
          </Typography>
          <Chip tone={tone}>{dwell.own ? 'our kit' : 'observed'}</Chip>
          {dwell.duration_s != null && (
            <Typography
              variant="caption"
              sx={{
                ml: 'auto',
                fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.66rem',
                fontWeight: 700,
                color: tone,
              }}
            >
              {formatSpan(dwell.duration_s)}
            </Typography>
          )}
        </Box>
        {pct > 0 && (
          <Box
            aria-hidden
            sx={{
              mt: 0.5,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.07)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 2, bgcolor: tone, opacity: 0.75 }} />
          </Box>
        )}
        {dwell.place_address && dwell.place_address !== dwell.place_name && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.35, fontSize: '0.62rem', color: '#c8cfd7' }}
            noWrap
          >
            {dwell.place_address}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 0.35, fontSize: '0.62rem', color: MUTED }}>
          {dwell.own ? dwell.label : 'unassigned device'}
          {dwell.start_at ? ` · ${formatWhen(dwell.start_at)}` : ''}
          {dwell.end_at ? ` – ${formatWhen(dwell.end_at)}` : dwell.start_at ? ' · still there' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

const Chip: React.FC<{ tone?: string; children: React.ReactNode }> = ({
  tone = MUTED,
  children,
}) => (
  <Box
    component="span"
    sx={{
      fontSize: '0.58rem',
      fontWeight: 700,
      color: tone,
      border: `1px solid ${EDGE}`,
      bgcolor: 'rgba(255,255,255,0.04)',
      px: 0.6,
      borderRadius: 1.1,
      lineHeight: 1.7,
    }}
  >
    {children}
  </Box>
);

export default MissionDwellsTab;
